import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Input, Layout, Menu, message, Modal, Select, Tag } from "antd";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import api from "../../../api/api";
import { Checkbox, FormControlLabel, Paper } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import DataTable from "../../../Components/DataTable/DataTable";

const PermissionAccess = () => {
  const { role: selectedRole } = useParams();
  const { headerOffset = 64 } = useOutletContext() ?? {};
  const navigate = useNavigate();
  const setSelectedRole = (key) => {
    navigate(key ? `/permission_manager/${key}` : "/permission_manager");
  };
  const [collapsed, setCollapsed] = useState(true);
  const [name, setName] = useState();
  const [navItems, setNavItems] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showOnlyChecked, setShowOnlyChecked] = useState(false);
  const [currentSelected, setCurrentSelected] = useState([]);
  const [subShowOnly, setSubShowOnly] = useState([]);
  const [search, setSearch] = useState();
  const [showUsers, setShowUsers] = useState(false);
  const [changeUserRole, setChangeUserRole] = useState(false);
  const [users, setUsers] = useState([]);
  const [copyFromRole, setCopyFromRole] = useState();
  const [changeUserRoleData, setChangeUserRoleData] = useState({});
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const groupIndexRef = useRef(null);

  const onSaveNewRole = () => {
    const permissions =
      roles
        .find((role) => role.type === copyFromRole)
        ?.permissions?.map((permission) => permission.action) ?? [];

    setSelectedRole();
    api
      .post("/role-manager", { name, action: "add", permissions })
      .then((res) => message.info("ذخیر رول با موفقیت انجام شد"))
      .catch(() => message.error("خطا در ذخیره رول"))
      .finally(() => loadData());
  };

  const handleSubShowOnly = (checked, group) => {
    if (checked) {
      setSubShowOnly((prev) => Array.from(new Set([...prev, group])));
    } else {
      setSubShowOnly((prev) =>
        Array.from(new Set(prev.filter((e) => e !== group)))
      );
    }
  };

  const loadData = () => {
    api
      .get("/role-manager")
      .then((res) => {
        setGroups(res.data.groups);
        setUsers(res.data.users);
        setPermissions(res.data.permissionsDictionary);
        setRoles(res.data.roles);
        const roleList = res.data.roles.map((e) => ({
          icon: <UserOutlined />,
          label: e.name,
          key: e.type,
        }));
        setNavItems([
          {
            icon: <PlusOutlined />,
            label: <span>افزودن رول</span>,
            key: "new",
          },
          {
            icon: <GroupsIcon />,
            label: <span>گزارش رول ها</span>,
            key: "users_list",
          },
          ...roleList,
        ]);
      })
      .catch(() => message.error("خطا در دریافت لیست رول ها"));
  };

  const updateRole = (roleType, permission, action) => {
    if (action === "add") {
      setCurrentSelected((prev) => [...prev, permission]);
    } else if (action === "delete") {
      setCurrentSelected((prev) => prev.filter((e) => e !== permission));
    }

    api
      .put("/role-manager", {
        permission,
        action,
        roleType,
      })
      .then(() => {})
      .catch(() => {
        message.error("خطا در اپدیت دسترسی");
        if (action === "add") {
          setCurrentSelected((prev) => prev.filter((e) => e !== permission));
        } else if (action === "delete") {
          setCurrentSelected((prev) => [...prev, permission]);
        }
      });
  };

  const permissionSelected = (permission) => {
    return currentSelected.includes(permission);
  };
  const deleteRole = (type) => {
    api
      .delete(`/role-manager/${type}`)
      .then(() => {
        message.success("با موفقیت حذف شد");
        loadData();
      })
      .catch(() => message.error("خطا در حذف رول"));
  };

  const onUpdateUserRole = () => {
    if (!changeUserRoleData?.type || !changeUserRoleData?.userId) {
      message.error("فیلد ها را کامل کنید");
      return;
    }
    setChangeUserRole(false);
    api
      .post(`/change-user-role`, changeUserRoleData)
      .then(() => {
        message.success("با موفقیت انجام شد");
      })
      .catch(() => message.error("خطا در درخواست"))
      .finally(() => {
        loadData();
        setChangeUserRoleData({});
      });
  };

  useEffect(() => loadData(), [selectedRole]);

  useEffect(() => {
    setActiveGroupIndex(0);
    if (!groups.length) return;

    const updateActiveGroup = () => {
      const indexHeight = groupIndexRef.current?.offsetHeight ?? 0;
      const offset = headerOffset + indexHeight + 8;
      let current = 0;
      groups.forEach((_, i) => {
        const el = document.getElementById(`permission-group-${i}`);
        if (el && el.getBoundingClientRect().top <= offset) {
          current = i;
        }
      });
      setActiveGroupIndex(current);
    };

    updateActiveGroup();
    window.addEventListener("scroll", updateActiveGroup, { passive: true });
    return () => window.removeEventListener("scroll", updateActiveGroup);
  }, [groups, selectedRole, headerOffset]);

  useEffect(() => {
    setCurrentSelected(
      roles
        .find((role) => role.type === selectedRole)
        ?.permissions?.map((e) => e.action) ?? []
    );
  }, [roles, selectedRole]);

  const usersListTableData = useMemo(() => {
    const tempRoles = roles
      .map((e) => e.type)
      .filter(
        (e) => e && !["authenticated", "public"].includes(e?.toLowerCase())
      );

    return tempRoles.map((role) => ({
      role,
      users: users.filter((user) => user?.role?.type === role),
    }));
  }, [users, roles]);

  const usersListColumns = [
    {
      title: "رول",
      dataIndex: "role",
      searchable: true,
    },
    {
      title: "کاربران متصل",
      dataIndex: "users",
      render: (_, record) => {
        return record.users.map((e) => (
          <Tag>{`${e.firstname} ${e.lastname}`}</Tag>
        ));
      },
    },
    {
      title: "عملیات",
      width: "15rem",
      align: "center",
      dataIndex: "delete",
      render: (_, record) => {
        return (
          <Button
            onClick={() => deleteRole(record.role)}
            disabled={record.users.length > 0}
            danger
          >
            حذف
          </Button>
        );
      },
    },
  ];

  return (
    <Layout style={{ background: "white" }}>
      <Layout.Sider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        style={{
          padding: "1rem 0",
          background: "white",
        }}
        width={"20rem"}
      >
        <Menu
          mode="inline"
          onClick={(e) => {
            setSelectedRole(e.key);
          }}
          selectedKeys={selectedRole ? [selectedRole] : []}
          style={{ height: "100%" }}
          items={navItems}
        />
      </Layout.Sider>

      <Layout.Content
        style={{
          padding: "0 24px",
          height: "100%",
          borderRadius: "6px",
        }}
      >
        {selectedRole &&
          selectedRole !== "new" &&
          selectedRole !== "users_list" && (
            <>
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "inline-block",
                  marginTop: "1.5rem",
                }}
              >
                <h2 style={{ textAlign: "center" }}>
                  {roles.find((role) => role.type === selectedRole)?.name ??
                    selectedRole}
                </h2>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <FormControlLabel
                  style={{ margin: "0" }}
                  control={
                    <Checkbox
                      checked={showOnlyChecked}
                      onChange={(event) =>
                        setShowOnlyChecked(event.target.checked)
                      }
                    />
                  }
                  label={"نمایش انتخاب شده ها"}
                />

                <div style={{ gap: "0.5rem", display: "flex" }}>
                  {!["public", "authenticated"].includes(selectedRole) && (
                    <Button onClick={() => setShowUsers(true)}>کاربران</Button>
                  )}
                  <Input
                    onInput={(e) => setSearch(e.target.value)}
                    placeholder="جستجو"
                    style={{ width: "15rem" }}
                  />
                </div>
              </div>

              <div
                ref={groupIndexRef}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.25rem",
                  marginTop: "1rem",
                  position: "sticky",
                  top: headerOffset,
                  zIndex: 9,
                  background: "white",
                  padding: "0.5rem 24px",
                  marginLeft: "-24px",
                  marginRight: "-24px",
                  borderBottom: "1px solid #f0f0f0",
                  borderTop: "1px solid #f0f0f0",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                {groups.map((e, i) => (
                  <Tag
                    key={i}
                    color={activeGroupIndex === i ? "blue" : undefined}
                    style={{
                      cursor: "pointer",
                      padding: "0.2rem 0.5rem",
                      margin: 0,
                      fontSize: "14px"
                    }}
                    onClick={() => {
                      setActiveGroupIndex(i);
                      const el = document.getElementById(
                        `permission-group-${i}`
                      );
                      if (!el) return;
                      const indexHeight =
                        groupIndexRef.current?.offsetHeight ?? 0;
                      const top =
                        el.getBoundingClientRect().top +
                        window.scrollY -
                        headerOffset -
                        indexHeight;
                      window.scrollTo({ top, behavior: "smooth" });
                    }}
                  >
                    {e.name}
                  </Tag>
                ))}
              </div>

              {groups.map((e, i) => {
                const subShowOnlyThis = subShowOnly.includes(e.name);
                const visiblePermissions = e.permissions
                  .filter((per) => {
                    const label = permissions[per];
                    if (!label) return false;
                    if (search) return label.search(search) !== -1;
                    return true;
                  })
                  .filter((per) => {
                    if (!(showOnlyChecked || subShowOnlyThis)) return true;
                    return permissionSelected(per);
                  })
                  .slice()
                  .sort((a, b) =>
                    String(permissions[a] ?? "").localeCompare(
                      String(permissions[b] ?? ""),
                      "fa"
                    )
                  );

                return (
                  <Paper
                    id={`permission-group-${i}`}
                    key={i}
                    elevation={3}
                    style={{
                      padding: "0.5rem",
                      marginTop: "1rem",
                      marginBottom: "1rem",
                      scrollMarginTop: `${headerOffset + 76}px`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginRight: "1rem",
                        marginLeft: "1rem",
                      }}
                    >
                      <h3>{e.name}</h3>

                      <FormControlLabel
                        style={{ margin: "0" }}
                        control={
                          <Checkbox
                            checked={subShowOnlyThis}
                            onChange={(event) =>
                              handleSubShowOnly(event.target.checked, e.name)
                            }
                          />
                        }
                        label={"نمایش انتخاب شده ها"}
                      />
                    </div>

                    {visiblePermissions.length ? (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(18rem, 1fr))",
                          gap: "0.25rem 0.75rem",
                          padding: "0.25rem 1rem 0.75rem",
                        }}
                      >
                        {visiblePermissions.map((per) => {
                          const selected = permissionSelected(per);
                          return (
                            <FormControlLabel
                              key={per}
                              style={{
                                margin: 0,
                                width: "100%",
                                padding: "0.15rem 0.35rem",
                                borderRadius: "6px",
                                background: selected ? "#f0f7ff" : "#f0f0f0",
                                cursor: "pointer",
                              }}
                              control={
                                <Checkbox
                                  checked={selected}
                                  onChange={(event) => {
                                    updateRole(
                                      selectedRole,
                                      per,
                                      event.target.checked ? "add" : "delete"
                                    );
                                  }}
                                />
                              }
                              label={permissions[per]}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        style={{
                          padding: "0.75rem 1rem",
                          color: "#8c8c8c",
                        }}
                      >
                        دسترسی‌ای یافت نشد
                      </div>
                    )}
                  </Paper>
                );
              })}
            </>
          )}

        {selectedRole === "users_list" && (
          <>
            <DataTable
              header={{
                title: (
                  <>
                    {"گزارش رول ها"}
                    <Button
                      onClick={() => setChangeUserRole(true)}
                      style={{ marginRight: "1rem" }}
                    >
                      تعویض رول کاربر
                    </Button>
                  </>
                ),
              }}
              dataSource={usersListTableData}
              setDataSource={() => {}}
              columns={usersListColumns}
            />
          </>
        )}
      </Layout.Content>

      {/* add role */}
      <Modal
        title="افزودن رول"
        open={selectedRole === "new"}
        destroyOnClose
        onOk={() => onSaveNewRole()}
        onCancel={() => setSelectedRole()}
      >
        <Input
          style={{ marginTop: "0.7rem", marginBottom: "0.4rem" }}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <Select
          value={copyFromRole}
          onChange={(val) => setCopyFromRole(val)}
          style={{ width: "100%", marginBottom: "0.7rem" }}
          allowClear={true}
          placeholder="کپی دسترسی ها از"
          options={roles.map((role) => ({
            label: role.type,
            value: role.type,
          }))}
        />
      </Modal>

      {/* show users */}
      <Modal
        title={`کاربران متصل به ${selectedRole}`}
        open={showUsers}
        destroyOnClose
        onCancel={() => setShowUsers()}
        footer={null}
      >
        {users
          .filter((e) => e?.role?.type === selectedRole)
          .map((user) => (
            <Tag
              style={{ margin: "0.5rem" }}
            >{`${user.id}: ${user.firstname} ${user.lastname}`}</Tag>
          ))}
      </Modal>

      {/* change user role */}
      <Modal
        title="تغییر رول کاربر"
        open={changeUserRole}
        destroyOnClose
        onOk={() => onUpdateUserRole()}
        onCancel={() => {
          setChangeUserRole(false);
          setChangeUserRoleData({});
        }}
      >
        <Select
          value={changeUserRoleData?.userId}
          onChange={(val) =>
            setChangeUserRoleData((prev) => ({ ...prev, userId: val }))
          }
          style={{ width: "100%", marginBottom: "0.7rem" }}
          allowClear={true}
          placeholder="کاربر را انتخاب کنید"
          options={users.map((user) => ({
            label: `${user.firstname} ${user.lastname}`,
            value: user.id,
          }))}
        />

        <Select
          value={changeUserRoleData?.type}
          onChange={(val) =>
            setChangeUserRoleData((prev) => ({ ...prev, type: val }))
          }
          style={{ width: "100%", marginBottom: "0.7rem" }}
          allowClear={true}
          placeholder="رول را انتخاب کنید"
          options={roles.map((role) => ({
            label: role.type,
            value: role.type,
          }))}
        />
      </Modal>
    </Layout>
  );
};

export default memo(PermissionAccess);
